import { createFileRoute } from '@tanstack/react-router';
import { PageContent } from '@/components/page-content';
import {
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyTitle,
} from '@/components/ui/empty';
import { SearchConsoleSettings } from '@/components/settings/search-console-settings';
import { SearchConsoleService } from '@/api/search-console-service';
import { useFeatures } from '@/hooks/use-features';

// Google redirects back here with ?code&state. The exchange runs in the loader
// rather than an effect, so it fires exactly once per navigation.
export const Route = createFileRoute(
  '/_auth/_side-layout/project-settings/search-console',
)({
  validateSearch: (
    search: Record<string, unknown>,
  ): { code?: string; state?: string } => ({
    code: typeof search.code === 'string' ? search.code : undefined,
    state: typeof search.state === 'string' ? search.state : undefined,
  }),
  loaderDeps: ({ search }) => ({ code: search.code, state: search.state }),
  loader: async ({ deps }) => {
    if (!deps.code || !deps.state) return { pending: null };
    try {
      return {
        pending: await SearchConsoleService.completeCallback({
          code: deps.code,
          state: deps.state,
        }),
      };
    } catch (error) {
      return {
        pending: null,
        failedReason:
          error instanceof Error && error.message
            ? error.message
            : 'Google authorization did not complete.',
      };
    }
  },
  component: SearchConsoleSettingsPage,
});

function SearchConsoleSettingsPage() {
  const { pending, failedReason } = Route.useLoaderData() as {
    pending: Awaited<
      ReturnType<typeof SearchConsoleService.completeCallback>
    > | null;
    failedReason?: string;
  };
  const { isEnabled } = useFeatures();

  // The tab is hidden when the flag is off, so this only catches a direct URL.
  if (!isEnabled('searchConsole')) {
    return (
      <PageContent>
        <Empty className="border">
          <EmptyHeader>
            <EmptyTitle>Search Console: Coming Soon</EmptyTitle>
            <EmptyDescription>
              Connecting Google Search Console is not available for your
              organization yet.
            </EmptyDescription>
          </EmptyHeader>
        </Empty>
      </PageContent>
    );
  }

  return (
    <PageContent>
      <SearchConsoleSettings
        initialPending={pending}
        callbackError={failedReason}
      />
    </PageContent>
  );
}
